/**
 * Three-column flow: User → Policy-gated agent → DEX. Each column
 * has a header, body, and a live indicator showing what's happening.
 */
export function HowItWorks() {
  return (
    <section id="how" className="ax-section ax-grain">
      <div className="ax-section-head">
        <div className="ax-eyebrow">03 · How it works</div>
        <h2 className="ax-h2">
          One owner key. One policy.<br />
          <span style={{ color: "var(--at-fg-dim)" }}>Zero trust in the agent.</span>
        </h2>
      </div>

      <div className="ax-flow">
        <Column
          step="01"
          title="Your machine"
          tagline="The only place your owner key lives"
          items={[
            ["CLI", "One command, local install"],
            ["Keystore", "scrypt + AES-GCM, mode 0600"],
            ["Audit log", "SHA-256 hash chain, SQLite"],
            ["Policy", "Local caps, leverage + daily loss"],
          ]}
        />
        <Arrow />
        <Column
          step="02"
          title="Validated agent"
          tagline="MCP into 8 runtimes. SDK for custom clients."
          items={[
            ["MCP", "30+ typed tools · 8 runtimes"],
            ["Intent check", "Leverage · size · slippage · LTV"],
            ["RiskGuard", "Pre-signature: passes or rejects"],
            ["Rejections", "Typed codes, agent-readable"],
          ]}
          highlight
        />
        <Arrow />
        <Column
          step="03"
          title="Your venues"
          tagline="Perps · spot · lending. Six EVM chains."
          items={[
            ["Hyperliquid", "Perps · default-on · Builder Code"],
            ["Uniswap V3", "Spot · execute opt-in flag"],
            ["Aave V3", "Supply/repay live · borrow gated"],
            ["GMX v2", "Read-side · execute on roadmap"],
          ]}
        />
      </div>

      <div className="ax-flow-caption">
        The agent never sees your owner key.<br />
        The venue never sees a forbidden intent.
      </div>

      <style>{`
        .ax-flow {
          display: grid;
          grid-template-columns: 1fr 32px 1fr 32px 1fr;
          gap: 0;
          align-items: stretch;
          margin-bottom: 40px;
        }
        @media (max-width: 960px) {
          .ax-flow { grid-template-columns: 1fr; gap: 8px; }
        }

        .ax-flow-caption {
          text-align: center;
          font-family: var(--at-font-mono);
          font-size: 13px;
          line-height: 1.7;
          color: var(--at-muted);
          padding-top: 32px;
          border-top: 1px solid var(--at-hairline);
          max-width: 640px;
          margin: 0 auto;
        }
      `}</style>
    </section>
  );
}

function Column({
  step, title, tagline, items, highlight,
}: {
  step: string; title: string; tagline: string;
  items: [string, string][]; highlight?: boolean;
}) {
  return (
    <div className={"ax-flow-col" + (highlight ? " highlight" : "")}>
      <div className="ax-flow-step">{step}</div>
      <h3 className="ax-flow-title">{title}</h3>
      <div className="ax-flow-tagline">{tagline}</div>
      <div className="ax-flow-items">
        {items.map(([k, v]) => (
          <div className="ax-flow-item" key={k}>
            <div className="ax-flow-item-k">{k}</div>
            <div className="ax-flow-item-v">{v}</div>
          </div>
        ))}
      </div>
      <style>{`
        .ax-flow-col {
          background: var(--at-surface);
          border: 1px solid var(--at-hairline);
          border-radius: 14px;
          padding: 28px 26px;
          display: flex; flex-direction: column;
        }
        .ax-flow-col.highlight {
          border-color: rgba(74, 222, 128, 0.3);
          background: linear-gradient(180deg, rgba(74,222,128,0.04), var(--at-surface) 60%);
        }
        .ax-flow-step {
          font-family: var(--at-font-mono);
          font-size: 11px;
          letter-spacing: 0.3em;
          color: var(--at-accent);
          margin-bottom: 16px;
        }
        .ax-flow-title {
          font-size: 20px; font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--at-fg);
          margin: 0 0 6px;
        }
        .ax-flow-tagline {
          font-size: 13px;
          color: var(--at-muted);
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid var(--at-hairline);
        }
        .ax-flow-items { display: flex; flex-direction: column; gap: 14px; }
        .ax-flow-item {
          display: grid; grid-template-columns: 110px 1fr; gap: 12px;
          font-size: 13px;
        }
        .ax-flow-item-k {
          color: var(--at-fg);
          font-weight: 500;
        }
        .ax-flow-item-v {
          font-family: var(--at-font-mono);
          font-size: 12px;
          color: var(--at-muted);
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
}

function Arrow() {
  return (
    <div className="ax-arrow" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square">
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
      <style>{`
        .ax-arrow {
          display: flex; align-items: center; justify-content: center;
          color: var(--at-accent);
          opacity: 0.5;
        }
        @media (max-width: 960px) {
          .ax-arrow { transform: rotate(90deg); padding: 4px 0; }
        }
      `}</style>
    </div>
  );
}
